import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

// --- Gap Zone Report (Phase 1) ---
export interface SeoReportKeyword {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface SeoReport {
  generatedAt: string;
  dateRange: { startDate: string; endDate: string };
  summary: {
    totalImpressions: number;
    totalClicks: number;
    avgPosition: number;
    gapZoneCount: number;
    totalKeywords: number;
  };
  gapZoneKeywords: SeoReportKeyword[];
}

export interface SeoNoReport {
  status: 'no_report';
  message: string;
}

export type SeoReportResponse = SeoReport | SeoNoReport;

// --- Competitor Report (Phase 2) ---
export interface CompetitorEntry {
  domain: string;
  url: string;
  title: string;
  estimatedPosition: number;
  contentType: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  whyWinning: string;
  specificFix: string;
}

export interface PlatformIntentKeyword {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
  intentConfidence: 'high' | 'medium';
  competitors: CompetitorEntry[];
}

export interface CompetitorReport {
  generatedAt: string;
  platformIntentKeywords: PlatformIntentKeyword[];
  summary: {
    totalPlatformIntentKeywords: number;
    highThreatDomains: string[];
    mediumThreatDomains: string[];
    topOpportunity: string;
  };
}

export type CompetitorReportResponse = CompetitorReport | SeoNoReport;

// --- Content Plan (Phase 2) ---
export interface ContentPlanItem {
  title: string;
  targetKeyword: string;
  priority: 'P1' | 'P2';
  searchIntent: string;
  brief: string;
  estimatedImpressions: number;
  currentPosition: number;
  competitors: string[];
}

export interface ContentPlan {
  generatedAt: string;
  weekOf: string;
  items: ContentPlanItem[];
  summary: {
    p1Count: number;
    p2Count: number;
    totalEstimatedImpressions: number;
  };
}

export type ContentPlanResponse = ContentPlan | SeoNoReport;

// --- Async pipeline (Phase 3) ---
export interface PipelineStatus {
  running: boolean;
  latestMarkdown: { filename: string; mtime: string } | null;
}

export interface StartFullAnalysisAck {
  status?: string;
  message?: string;
  running?: boolean;
}

// --- SEO Drafts (Phase 4) ---
export type DraftStatus = 'draft' | 'approved' | 'rejected';

export interface SeoDraftListItem {
  slug: string;
  title: string;
  targetKeyword: string;
  status: DraftStatus;
  generatedAt: string;
  weekOf: string;
  priority: 'P1' | 'P2';
}

export interface SeoDraft extends SeoDraftListItem {
  metaTitle: string;
  metaDescription: string;
  bodyMarkdown: string;
  estimatedImpressions: number;
}

export interface DraftSkipped {
  status: 'skipped';
  reason: string;
}

export interface DraftError {
  status: 'error';
  message: string;
}

export type GenerateDraftResponse = SeoDraft | DraftSkipped | DraftError;

export const adminSeoApi = createApi({
  reducerPath: 'adminSeoApi',
  baseQuery: baseQuery,
  tagTypes: ['SeoReport', 'CompetitorReport', 'ContentPlan', 'PipelineStatus', 'Drafts'],
  endpoints: (builder) => ({
    getSeoReport: builder.query<SeoReportResponse, void>({
      query: () => ({
        url: '/admin/seo/report',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['SeoReport'],
    }),
    generateSeoReport: builder.mutation<SeoReport, void>({
      query: () => ({
        url: '/admin/seo/generate',
        method: 'POST',
        requiresAuth: true,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminSeoApi.util.updateQueryData('getSeoReport', undefined, () => data)
          );
        } catch {
          // Error handled by the component
        }
      },
    }),
    getCompetitorReport: builder.query<CompetitorReportResponse, void>({
      query: () => ({
        url: '/admin/seo/competitor-report',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['CompetitorReport'],
    }),
    getContentPlan: builder.query<ContentPlanResponse, void>({
      query: () => ({
        url: '/admin/seo/content-plan',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['ContentPlan'],
    }),
    startFullAnalysis: builder.mutation<StartFullAnalysisAck, void>({
      query: () => ({
        url: '/admin/seo/generate-full-async',
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['PipelineStatus'],
    }),
    getPipelineStatus: builder.query<PipelineStatus, void>({
      query: () => ({
        url: '/admin/seo/pipeline-status',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['PipelineStatus'],
    }),
    downloadLatestMarkdown: builder.mutation<string, void>({
      query: () => ({
        url: '/admin/seo/latest-markdown',
        method: 'GET',
        requiresAuth: true,
        responseHandler: (response: Response) => response.text(),
      }),
      invalidatesTags: ['SeoReport', 'CompetitorReport', 'ContentPlan'],
    }),
    getDrafts: builder.query<{ drafts: SeoDraftListItem[] }, void>({
      query: () => ({
        url: '/admin/seo/drafts',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['Drafts'],
    }),
    getDraft: builder.query<SeoDraft, string>({
      query: (slug) => ({
        url: `/admin/seo/drafts/${slug}`,
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: (_result, _error, slug) => [{ type: 'Drafts', id: slug }],
    }),
    generateDraft: builder.mutation<GenerateDraftResponse, void>({
      query: () => ({
        url: '/admin/seo/drafts/generate',
        method: 'POST',
        requiresAuth: true,
        // Generation is a synchronous Anthropic call; allow up to 120s
        timeout: 120000,
      }),
      invalidatesTags: ['Drafts'],
    }),
    updateDraftStatus: builder.mutation<SeoDraft, { slug: string; status: 'approved' | 'rejected' }>({
      query: ({ slug, status }) => ({
        url: `/admin/seo/drafts/${slug}/status`,
        method: 'POST',
        body: { status },
        requiresAuth: true,
      }),
      invalidatesTags: (_result, _error, { slug }) => ['Drafts', { type: 'Drafts', id: slug }],
    }),
  }),
});

export const {
  useGetSeoReportQuery,
  useGenerateSeoReportMutation,
  useGetCompetitorReportQuery,
  useGetContentPlanQuery,
  useStartFullAnalysisMutation,
  useGetPipelineStatusQuery,
  useDownloadLatestMarkdownMutation,
  useGetDraftsQuery,
  useGetDraftQuery,
  useGenerateDraftMutation,
  useUpdateDraftStatusMutation,
} = adminSeoApi;
